import React, { useEffect, useState } from 'react';
import './ExerciseDetailModal.css';
import ImageViewerModal from '../ImageViewerModal';
import { useModal } from '../../services/ModalService';
import ModalLoading from '../ModalLoading';

const ExerciseDetailModal = ({ isOpen, exerciseId, removeExercise, addExercise, close, backendService }) => {
  const [exerciseDetails, setExerciseDetails] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const modalService = useModal();

  useEffect(() => {
    if (!exerciseId) {
      setExerciseDetails(null);
      setImageUrls([]);
      return;
    }

    const fetchExerciseImages = async (images) => {
      try {
        const urls = await backendService.fetchExerciseImages(images);
        setImageUrls(urls);
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    };

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const data = await backendService.fetchExerciseDetails(exerciseId);
        setExerciseDetails(data);
        fetchExerciseImages(data.images);
      } catch (error) {
        setExerciseDetails(null);
        setImageUrls([]);
        if (error.response && error.response.status === 404) {
          console.error('Exercise not found:', error);
        } else {
          console.error('Error fetching exercise details:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [exerciseId, backendService]);


  if (loading) {
    return <div className="exercise-detail-parent exercise-detail-pending"><ModalLoading text="Loading exercise details..." /></div>;
  }

  if (!exerciseDetails) {
    return (
      <div className="exercise-detail-parent">
        <div className="exercise-detail-empty">No details available.</div>
      </div>
    );
  }

  const { name, instructions, doubleWeightForVolume, doubleRepsForVolume } = exerciseDetails;

  const hasLoggingHints = doubleWeightForVolume || doubleRepsForVolume;

  return (
    <div className="exercise-detail-parent">
      <div className="exercise-detail-name">{name}</div>
      {removeExercise && (
        <div className="center-button exercise-detail-action">
          <button onClick={
            () => {
              removeExercise();
              close();
            }
          }>
            Remove Exercise
          </button>
        </div>
      )}
      {addExercise && (
        <div className="center-button exercise-detail-action">
          <button onClick={
            () => {
              addExercise(exerciseId);
              close();
            }
          }>
            Re-add Exercise
          </button>
        </div>
      )}
      {hasLoggingHints && (
        <div className="exercise-detail-logging-hints">
          <div className="exercise-detail-logging-hints-title">Logging tips</div>
          {doubleWeightForVolume && (
            <div className="exercise-detail-logging-hint">
              <span className="exercise-detail-hint-icon">&#9878;</span>
              <span>Enter the weight of <strong>one</strong> dumbbell/kettlebell. Volume is calculated using both sides combined.</span>
            </div>
          )}
          {doubleRepsForVolume && (
            <div className="exercise-detail-logging-hint">
              <span className="exercise-detail-hint-icon">&#9878;</span>
              <span>Enter reps <strong>per side</strong>. Volume is calculated for both sides combined.</span>
            </div>
          )}
        </div>
      )}
      <div className="exercise-detail-images-container">
        {imageUrls.map((url, index) => {
          const expand = () => modalService.open(ImageViewerModal, {
            images: imageUrls,
            initialIndex: index,
            alt: `Exercise ${name}`
          });
          return (
            <img
              key={index}
              src={url}
              className="exercise-detail-image"
              alt={`Exercise ${name}`}
              onClick={expand}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  expand();
                }
              }}
              role="button"
              tabIndex={0}
            />
          );
        })}
      </div>
      <div className="exercise-detail-instructions">
        {instructions.map((instruction, index) => (
          <div key={index} className="exercise-detail-instructions-item">
            <div className="exercise-detail-instructions-number">{index + 1}</div>
            <div className="exercise-detail-instruction-text">{instruction}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExerciseDetailModal;